import { z } from "zod";

import {
  analysisSnapshotSchema,
  analysisSnapshotsQuerySchema,
  createProductionApiResponseSchema,
  dataCoverageSchema,
  dailySettlementSchema,
  fxQualityFlagSchema,
  futuresContractSchema,
  fxRateSchema,
  fxRatesQuerySchema,
  listContractsQuerySchema,
  isSyntheticSourceId,
  settlementsQuerySchema,
  settlementQualityFlagSchema,
  type AnalysisSnapshot,
  type AnalysisSnapshotsQuery,
  type DailySettlement,
  type DataCoverage,
  type FuturesContract,
  type FxRate,
  type FxRatesQuery,
  type ListContractsQuery,
  type SettlementsQuery,
} from "../contracts/schemas";
import {
  AdapterConfigurationError,
  AdapterHttpError,
  AdapterValidationError,
} from "./errors";
import type { MarketDataAdapter } from "./market-data-adapter";

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface ValidatedHttpMarketDataAdapterConfig {
  baseUrl?: string | null;
  bearerToken?: string | null;
  fetch?: FetchLike;
  endpoints?: Partial<{
    contracts: string;
    settlements: string;
    fxRates: string;
    coverage: string;
    analysisSnapshots: string;
  }>;
}

interface HttpAdapterEndpoints {
  contracts: string;
  settlements: string;
  fxRates: string;
  coverage: string;
  analysisSnapshots: string;
}

const DEFAULT_ENDPOINTS: HttpAdapterEndpoints = {
  contracts: "v1/contracts",
  settlements: "v1/settlements",
  fxRates: "v1/fx-rates",
  coverage: "v1/coverage",
  analysisSnapshots: "v1/analysis-snapshots",
} as const;

export class ValidatedHttpMarketDataAdapter implements MarketDataAdapter {
  readonly mode = "production" as const;

  private readonly baseUrl: URL;
  private readonly bearerToken?: string;
  private readonly fetchImpl: FetchLike;
  private readonly endpoints: HttpAdapterEndpoints;
  private establishedSourceId?: string;

  constructor(config: ValidatedHttpMarketDataAdapterConfig) {
    if (!config.baseUrl?.trim()) {
      throw new AdapterConfigurationError(
        "Production market-data API base URL is not configured; demo fallback is disabled",
      );
    }

    try {
      this.baseUrl = new URL(ensureTrailingSlash(config.baseUrl));
    } catch (error) {
      throw new AdapterConfigurationError("Production market-data API base URL is invalid", {
        cause: error,
      });
    }

    if (!new Set(["http:", "https:"]).has(this.baseUrl.protocol)) {
      throw new AdapterConfigurationError(
        "Production market-data API base URL must use HTTP or HTTPS",
      );
    }

    const globalFetch = globalThis.fetch?.bind(globalThis);
    if (!config.fetch && !globalFetch) {
      throw new AdapterConfigurationError("No Fetch implementation is available");
    }

    this.fetchImpl = config.fetch ?? globalFetch!;
    this.bearerToken = config.bearerToken?.trim() || undefined;
    if (this.bearerToken && this.baseUrl.protocol !== "https:") {
      throw new AdapterConfigurationError(
        "Bearer-authenticated production market-data APIs must use HTTPS",
      );
    }
    const endpoints = { ...DEFAULT_ENDPOINTS, ...config.endpoints };
    for (const [name, path] of Object.entries(endpoints)) validateRelativeEndpoint(name, path);
    this.endpoints = endpoints;
  }

  async listContracts(query: ListContractsQuery = {}): Promise<FuturesContract[]> {
    const parsed = listContractsQuerySchema.parse(query);
    const url = this.endpoint(this.endpoints.contracts);
    appendMany(url, "instrumentId", parsed.instrumentIds);
    appendOptional(url, "deliveryYearFrom", parsed.deliveryYearFrom);
    appendOptional(url, "deliveryYearTo", parsed.deliveryYearTo);
    url.searchParams.set("enabledOnly", String(parsed.enabledOnly));

    const records = await this.getProductionData(url, z.array(futuresContractSchema));
    assertContractQueryScope(records, parsed);
    return records;
  }

  async getSettlements(query: SettlementsQuery): Promise<DailySettlement[]> {
    const parsed = settlementsQuerySchema.parse(query);
    const url = this.endpoint(this.endpoints.settlements);
    appendMany(url, "contractId", parsed.contractIds);
    url.searchParams.set("startDate", parsed.startDate);
    url.searchParams.set("endDate", parsed.endDate);
    appendOptional(url, "asOfTimestamp", parsed.asOfTimestamp);
    url.searchParams.set("purpose", parsed.purpose);

    const records = await this.getProductionData(url, z.array(dailySettlementSchema), {}, {
      validateRecordSources: true,
      asOfTimestamp: parsed.asOfTimestamp,
    });
    assertSettlementQueryScope(records, parsed);
    assertKnownSettlementQualityFlags(records);
    return records;
  }

  async getFxRates(query: FxRatesQuery): Promise<FxRate[]> {
    const parsed = fxRatesQuerySchema.parse(query);
    const url = this.endpoint(this.endpoints.fxRates);
    appendMany(url, "pair", parsed.pairs);
    url.searchParams.set("startDate", parsed.startDate);
    url.searchParams.set("endDate", parsed.endDate);
    appendOptional(url, "asOfTimestamp", parsed.asOfTimestamp);
    url.searchParams.set("purpose", parsed.purpose);

    const records = await this.getProductionData(url, z.array(fxRateSchema), {}, {
      validateRecordSources: true,
      asOfTimestamp: parsed.asOfTimestamp,
    });
    assertFxQueryScope(records, parsed);
    assertKnownFxQualityFlags(records);
    return records;
  }

  async getCoverage(): Promise<DataCoverage> {
    const coverage = await this.getProductionData(
      this.endpoint(this.endpoints.coverage),
      dataCoverageSchema,
    );
    if (coverage.sourceId !== this.establishedSourceId) {
      throw new AdapterValidationError(
        `Coverage source ${coverage.sourceId} does not match API source ${this.establishedSourceId}`,
      );
    }
    return coverage;
  }

  async getAnalysisSnapshots(
    query: AnalysisSnapshotsQuery = {},
  ): Promise<AnalysisSnapshot[]> {
    const parsed = analysisSnapshotsQuerySchema.parse(query);
    const url = this.endpoint(this.endpoints.analysisSnapshots);
    appendMany(url, "pairPresetId", parsed.pairPresetIds);
    appendOptional(url, "asOfDate", parsed.asOfDate);
    appendOptional(url, "analysisConfigId", parsed.analysisConfigId);

    return this.getProductionData(url, z.array(analysisSnapshotSchema));
  }

  async saveAnalysisSnapshot(snapshot: AnalysisSnapshot): Promise<AnalysisSnapshot> {
    const parsed = analysisSnapshotSchema.parse(snapshot);

    return this.getProductionData(
      this.endpoint(this.endpoints.analysisSnapshots),
      analysisSnapshotSchema,
      {
        method: "POST",
        body: JSON.stringify(parsed),
      },
    );
  }

  private endpoint(path: string): URL {
    const url = new URL(path.replace(/^\/+/, ""), this.baseUrl);
    if (url.origin !== this.baseUrl.origin) {
      throw new AdapterConfigurationError("Market-data endpoint must stay on the configured origin");
    }
    return url;
  }

  private async getProductionData<T extends z.ZodTypeAny>(
    url: URL,
    dataSchema: T,
    init: RequestInit = {},
    options: {
      readonly validateRecordSources?: boolean;
      readonly asOfTimestamp?: string;
    } = {},
  ): Promise<z.output<T>> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    if (init.body !== undefined) headers.set("content-type", "application/json");
    if (this.bearerToken) headers.set("authorization", `Bearer ${this.bearerToken}`);

    let response: Response;
    try {
      response = await this.fetchImpl(url, { ...init, headers });
    } catch (error) {
      throw new AdapterHttpError(0, `Market-data API request failed: ${url.pathname}`, {
        cause: error,
      });
    }

    if (!response.ok) {
      throw new AdapterHttpError(
        response.status,
        `Market-data API returned ${response.status} for ${url.pathname}`,
      );
    }

    let value: unknown;
    try {
      value = await response.json();
    } catch (error) {
      throw new AdapterValidationError("Market-data API returned invalid JSON", { cause: error });
    }

    const envelope = createProductionApiResponseSchema(z.unknown()).safeParse(value);
    if (!envelope.success) {
      throw new AdapterValidationError(
        `Market-data API response failed production validation: ${envelope.error.message}`,
        { cause: envelope.error },
      );
    }

    const sourceId = envelope.data.source.id;
    if (this.establishedSourceId && this.establishedSourceId !== sourceId) {
      throw new AdapterValidationError(
        `Market-data API changed source from ${this.establishedSourceId} to ${sourceId}`,
      );
    }
    this.establishedSourceId = sourceId;

    const data = dataSchema.safeParse(envelope.data.data);
    if (!data.success) {
      throw new AdapterValidationError(
        `Market-data API payload failed validation: ${data.error.message}`,
        { cause: data.error },
      );
    }

    if (options.validateRecordSources) {
      assertProductionRecordSources(data.data, sourceId);
    }
    if (options.asOfTimestamp) {
      assertNoRecordsAfterCutoff(data.data, options.asOfTimestamp);
    }

    return data.data;
  }
}

function ensureTrailingSlash(value: string): string {
  return value.endsWith("/") ? value : `${value}/`;
}

function validateRelativeEndpoint(name: string, value: string): void {
  const path = value.trim();
  if (
    path.length === 0 ||
    /^[a-z][a-z\d+.-]*:/i.test(path) ||
    path.startsWith("//") ||
    path.includes("\\")
  ) {
    throw new AdapterConfigurationError(
      `Market-data endpoint ${name} must be a non-empty relative same-origin path`,
    );
  }
}

function assertProductionRecordSources(value: unknown, expectedSourceId: string): void {
  if (!Array.isArray(value)) return;
  value.forEach((record, index) => {
    if (!record || typeof record !== "object" || !("source" in record)) return;
    const source = (record as { source?: unknown }).source;
    if (
      typeof source !== "string" ||
      source !== expectedSourceId ||
      isSyntheticSourceId(source)
    ) {
      throw new AdapterValidationError(
        `Production record ${index} source does not match API source ${expectedSourceId}`,
      );
    }
  });
}

function assertNoRecordsAfterCutoff(value: unknown, cutoff: string): void {
  if (!Array.isArray(value)) return;
  const cutoffTime = Date.parse(cutoff);
  value.forEach((record, index) => {
    if (!record || typeof record !== "object" || !("ingestedAt" in record)) return;
    const ingestedAt = (record as { ingestedAt?: unknown }).ingestedAt;
    if (typeof ingestedAt !== "string" || Date.parse(ingestedAt) > cutoffTime) {
      throw new AdapterValidationError(
        `Production record ${index} was ingested after the requested as-of cutoff`,
      );
    }
  });
}

function assertSettlementQueryScope(
  records: readonly DailySettlement[],
  query: ReturnType<typeof settlementsQuerySchema.parse>,
): void {
  const contractIds = new Set(query.contractIds);
  records.forEach((record, index) => {
    if (!contractIds.has(record.contractId)) {
      throw new AdapterValidationError(
        `Settlement record ${index} contract ${record.contractId} is outside the requested contract scope`,
      );
    }
    if (
      record.exchangeBusinessDate < query.startDate ||
      record.exchangeBusinessDate > query.endDate
    ) {
      throw new AdapterValidationError(
        `Settlement record ${index} business date ${record.exchangeBusinessDate} is outside the requested date scope`,
      );
    }
  });
}

function assertContractQueryScope(
  records: readonly FuturesContract[],
  query: ReturnType<typeof listContractsQuerySchema.parse>,
): void {
  const instrumentIds = query.instrumentIds ? new Set(query.instrumentIds) : null;
  records.forEach((record, index) => {
    if (instrumentIds && !instrumentIds.has(record.instrumentId)) {
      throw new AdapterValidationError(
        `Contract record ${index} instrument ${record.instrumentId} is outside the requested instrument scope`,
      );
    }
    if (
      query.deliveryYearFrom !== undefined &&
      record.deliveryYear < query.deliveryYearFrom
    ) {
      throw new AdapterValidationError(
        `Contract record ${index} delivery year ${record.deliveryYear} precedes the requested range`,
      );
    }
    if (query.deliveryYearTo !== undefined && record.deliveryYear > query.deliveryYearTo) {
      throw new AdapterValidationError(
        `Contract record ${index} delivery year ${record.deliveryYear} exceeds the requested range`,
      );
    }
    if (query.enabledOnly && !record.enabled) {
      throw new AdapterValidationError(
        `Contract record ${index} is disabled despite enabledOnly=true`,
      );
    }
  });
}

function assertKnownSettlementQualityFlags(records: readonly DailySettlement[]): void {
  records.forEach((record, index) => {
    const unknown = record.qualityFlags.filter(
      (flag) => !settlementQualityFlagSchema.safeParse(flag).success,
    );
    if (unknown.length > 0) {
      throw new AdapterValidationError(
        `Settlement record ${index} contains unsupported production quality flags: ${unknown.join(", ")}`,
      );
    }
  });
}

function assertKnownFxQualityFlags(records: readonly FxRate[]): void {
  records.forEach((record, index) => {
    const unknown = record.qualityFlags.filter(
      (flag) => !fxQualityFlagSchema.safeParse(flag).success,
    );
    if (unknown.length > 0) {
      throw new AdapterValidationError(
        `FX record ${index} contains unsupported production quality flags: ${unknown.join(", ")}`,
      );
    }
  });
}

function assertFxQueryScope(
  records: readonly FxRate[],
  query: ReturnType<typeof fxRatesQuerySchema.parse>,
): void {
  const pairs = new Set(query.pairs);
  records.forEach((record, index) => {
    if (!pairs.has(record.pair)) {
      throw new AdapterValidationError(
        `FX record ${index} pair ${record.pair} is outside the requested pair scope`,
      );
    }
    if (record.date < query.startDate || record.date > query.endDate) {
      throw new AdapterValidationError(
        `FX record ${index} date ${record.date} is outside the requested date scope`,
      );
    }
  });
}

function appendMany(url: URL, name: string, values: readonly string[] | undefined): void {
  for (const value of values ?? []) url.searchParams.append(name, value);
}

function appendOptional(
  url: URL,
  name: string,
  value: string | number | undefined,
): void {
  if (value !== undefined) url.searchParams.set(name, String(value));
}
